import React, { useState } from "react";

import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import twMerge from "@replo/design-system/utils/twMerge";
import useMeasure from "react-use-measure";
import { useInterval } from "replo-utils/react/use-interval";

import "devices.css";

const MOBILE_DEVICE_DIMENSIONS = {
  width: 430,
  height: 868,
  screenWidth: 390,
} as const;

const CellularIcon = () => (
  <svg
    width="17"
    height="10"
    viewBox="0 0 17 10"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6.59082 4.00098C7.0929 4.00098 7.5 4.40808 7.5 4.91016V9.0918C7.49986 9.59375 7.09281 10.001 6.59082 10.001H5.40918C4.90719 10.001 4.50014 9.59375 4.5 9.0918V4.91016C4.5 4.40808 4.9071 4.00098 5.40918 4.00098H6.59082ZM2 5.5C2.5522 5.5 2.99986 5.94784 3 6.5V9C3 9.55228 2.55228 10 2 10H1C0.44773 9.99998 0 9.55227 0 9V6.5C0.000144278 5.94785 0.447819 5.50002 1 5.5H2ZM11.0908 2C11.5928 2 11.9998 2.40727 12 2.90918V9.0918C11.9998 9.59367 11.5927 10 11.0908 10H9.90918C9.40729 9.99996 9.00024 9.59364 9 9.0918V2.90918C9.00019 2.40729 9.40726 2.00004 9.90918 2H11.0908ZM15.5908 0C16.0928 0 16.4999 0.407185 16.5 0.90918V9.09082C16.5 9.5929 16.0929 10 15.5908 10H14.4092C13.9071 9.99998 13.5 9.59289 13.5 9.09082V0.90918C13.5001 0.407195 13.9072 1.69688e-05 14.4092 0H15.5908Z"
      fill="black"
    />
  </svg>
);

const WifiIcon = () => (
  <svg
    width="17"
    height="12"
    viewBox="0 0 17 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8.18227 2.4544C10.5565 2.4545 12.8399 3.37214 14.5606 5.01765C14.6902 5.14469 14.8973 5.14309 15.0249 5.01406L16.2635 3.75668C16.3281 3.69124 16.3642 3.60259 16.3636 3.51036C16.3631 3.41812 16.326 3.32991 16.2607 3.26523C11.7444 -1.08841 4.61943 -1.08841 0.103152 3.26523C0.037731 3.32986 0.000611373 3.41805 8.14509e-06 3.51028C-0.00059519 3.60252 0.0353674 3.69119 0.0999378 3.75668L1.33889 5.01406C1.46643 5.14329 1.6737 5.14489 1.80319 5.01765C3.52411 3.37203 5.80781 2.45439 8.18227 2.4544ZM8.18227 6.54519C9.48675 6.54511 10.7447 7.03283 11.7116 7.91358C11.8424 8.03858 12.0484 8.03587 12.1759 7.90747L13.4131 6.65009C13.4783 6.58414 13.5144 6.49467 13.5135 6.4017C13.5125 6.30872 13.4746 6.22001 13.4081 6.1554C10.4635 3.40025 5.9035 3.40025 2.95893 6.1554C2.89242 6.22001 2.85447 6.30877 2.8536 6.40177C2.85272 6.49477 2.889 6.58423 2.95429 6.65009L4.1911 7.90747C4.31859 8.03587 4.52461 8.03858 4.6554 7.91358C5.62171 7.03341 6.87865 6.54573 8.18227 6.54519ZM10.5598 9.54602C10.626 9.4807 10.6624 9.39081 10.6605 9.29758C10.6586 9.20434 10.6186 9.11603 10.5498 9.05348C9.1831 7.89071 7.18143 7.89071 5.81471 9.05348C5.7459 9.11598 5.70578 9.20427 5.70382 9.2975C5.70186 9.39073 5.73823 9.48065 5.80435 9.54602L7.94476 11.7184C8.00749 11.7823 8.09302 11.8182 8.18227 11.8182C8.27151 11.8182 8.35704 11.7823 8.41977 11.7184L10.5598 9.54602Z"
      fill="black"
    />
  </svg>
);

const BatteryIcon = () => (
  <svg
    width="25"
    height="12"
    viewBox="0 0 25 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M1.74829 2.91355C1.74829 2.26986 2.2701 1.74805 2.91379 1.74805H18.9394C19.5831 1.74805 20.1049 2.26986 20.1049 2.91355V8.44968C20.1049 9.09337 19.5831 9.61518 18.9394 9.61518H2.91379C2.2701 9.61518 1.74829 9.09337 1.74829 8.44968V2.91355Z"
      fill="black"
    />
    <path
      opacity="0.4"
      d="M18.9395 0C20.5487 0 21.8535 1.30486 21.8535 2.91406V8.4502C21.8535 10.0594 20.5487 11.3643 18.9395 11.3643H2.91406C1.30484 11.3643 0 10.0594 0 8.4502V2.91406C2.48138e-05 1.30486 1.30486 0 2.91406 0H18.9395ZM2.91406 0.874023C1.78762 0.874023 0.874048 1.78763 0.874023 2.91406V8.4502C0.874023 9.57665 1.78761 10.4902 2.91406 10.4902H18.9395C20.0659 10.4902 20.9795 9.57665 20.9795 8.4502V2.91406C20.9795 1.78763 20.0659 0.874023 18.9395 0.874023H2.91406ZM22.7275 3.8418C23.5624 4.03939 24.1846 4.78734 24.1846 5.68262C24.1845 6.57786 23.5624 7.32587 22.7275 7.52344V3.8418Z"
      fill="black"
    />
  </svg>
);

type MobilePreviewProps = React.PropsWithChildren<{
  isLoading?: boolean;
  deviceColor?: "black" | "white" | "gold";
  className?: string;
  // Note (Ezra, Dec 3 2025): When cropToFit is true, the device scales based on container width only
  // and clips the overflow at the bottom, showing just the top portion of the phone (like in PageCard).
  // When false, the device scales to fit entirely within the container.
  cropToFit?: boolean;
}>;

export const MobilePreview: React.FC<MobilePreviewProps> = ({
  children,
  isLoading = false,
  deviceColor = "black",
  className,
  cropToFit = false,
}) => {
  const [containerRef, containerBounds] = useMeasure({ offsetSize: true });
  const [time, setTime] = useState(new Date());

  useInterval(() => {
    setTime(new Date());
  }, 1000);

  const formattedTime = time.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });

  const scale = React.useMemo(() => {
    if (containerBounds.height <= 0 || containerBounds.width <= 0) {
      return 0;
    }

    const scaleByWidth = containerBounds.width / MOBILE_DEVICE_DIMENSIONS.width;

    if (cropToFit) {
      return scaleByWidth;
    }

    const scaleByHeight =
      containerBounds.height / MOBILE_DEVICE_DIMENSIONS.height;
    return Math.min(scaleByHeight, scaleByWidth);
  }, [containerBounds.height, containerBounds.width, cropToFit]);

  return (
    <div
      ref={containerRef}
      className={twMerge(
        "h-full w-full overflow-hidden",
        cropToFit
          ? "flex items-start justify-center"
          : "flex items-center justify-center",
        className,
      )}
    >
      <div
        className={`device device-iphone-14 device-${deviceColor}`}
        style={{
          transform: `scale(${scale})`,
          transformOrigin: cropToFit ? "top center" : "center center",
          opacity: scale > 0 ? 1 : 0,
        }}
      >
        <div className="device-frame">
          <div className="device-screen flex flex-col overflow-hidden">
            {/* Note (Ezra, Nov 6 2025): Inline styles necessary for flexbox layout inside device preview due to devices.css specificity overriding Tailwind classes */}
            <div
              className="flex-shrink-0 h-7 bg-white px-8 py-2 text-md font-medium text-default"
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span className="pl-1">{formattedTime}</span>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <CellularIcon />
                <WifiIcon />
                <BatteryIcon />
              </div>
            </div>

            <div className="flex-1 min-h-0 h-full overflow-hidden">
              {isLoading ? <Skeleton className="w-full h-full" /> : children}
            </div>
          </div>
        </div>
        <div className="device-stripe" />
        <div className="device-header" />
        <div className="device-sensors" />
        <div className="device-btns" />
        <div className="device-power" />
      </div>
    </div>
  );
};
